// TOTALLY NORMAL TOWN — every feel / balance / economy number. docs/DESIGN.md explains them; the code reads them from here.

export const SIM_HZ = 20;                 // server-authoritative fixed step
export const SNAP_HZ = 15;                // snapshots to clients
export const INTERP_MS = 100;             // client render delay behind the server clock

// ---- the lobby and the round
export const SEATS = 8;                   // 1 Inspector + 7 Sneaks (humans take bot seats)
export const NPC_COUNT = 28;
export const INTRO_SEC = 5;
export const ROUND_SEC = 150;
export const RESULTS_SEC = 8;
export const VAULT_GOAL = 8;              // total stashes by all Sneaks → SNEAKS WIN
export const INSPECTOR_EVERY_SOLO = 3;    // one human at the table: Inspector every 3rd round
export const INSPECTOR_MIN_GAP = 2;       // a human waits ≥2 rounds between Inspector turns

// ---- movement (m/s)
export const WALK = 1.2;                  // 2026-10-04: matched to the retargeted clips (walk travels 0.67 m/s at 1×, run 1.71 m/s)
export const SPRINT = 2.8;                // was 1.5 / 3.4 / 1.8 / 3.2 — same relationships: sprint is a tell, the Inspector
export const INSPECTOR_WALK = 1.45;       // a little faster than a walking townie
export const PANIC_RUN = 2.6;
export const WALK_CLIP_MPS = 0.67;        // the renderer plays walk at speed / WALK_CLIP_MPS ...
export const RUN_CLIP_MPS = 1.71;         // ... and run at speed / RUN_CLIP_MPS
export const TURN_RATE = 10;              // rad/s heading slew for everyone
export const BODY_R = 0.32;               // collision radius of a townie

// ---- sneak verbs
export const GRAB_RANGE = 1.3;
export const GRAB_SEC = 1.0;
export const STASH_RANGE = 1.0;
export const STASH_SEC = 1.0;
export const GLINT_EVERY = 4.0;           // carried capsule sparkles every N s
export const RESPAWN_SEC = 4.0;
export const BLUNDER_SEC = 2.0;           // "?" puff duration

// ---- calls
export const CALL_GAP_MIN = 14;
export const CALL_GAP_MAX = 22;
export const CALL_FIRST_MIN = 6;          // no call in the first 6 s (onboarding round: exactly at 6 s)
export const CALL_WINDOW = 1.5;           // a Sneak must answer within this
export const CALL_PERFECT_LO = 0.25;      // PERFECT band = the NPC stagger band
export const CALL_PERFECT_HI = 0.70;
export const CALL_HOLD = 3.0;             // everyone holds the call this long
export const CALLS = ["dance", "freeze", "sit", "wave", "jump", "clap"] as const;
export type Call = typeof CALLS[number];

// ---- inspector
export const POP_RANGE = 16;
export const POP_PICK_R = 0.45;
export const POP_COOLDOWN = 1.2;
export const POP_CHARGES = 3;
export const POP_REGEN_SEC = 35;
export const WHISTLE_COOLDOWN = 30;
export const PANIC_R = 6;
export const PANIC_SEC = 3;
export const TUMBLE_SEC = 1.2;            // innocent ragdoll before getting up
export const ANGRY_SEC = 1.5;

// ---- capsules
export const STALLS = 6;
export const CAPSULES_MAX = 6;
export const CAPSULE_RESPAWN = 14;        // 2026-10-04: 15 → 14 with the slower clip-matched walk (balance gate a, docs/BALANCE.md)
export const RARITY = [                   // rolled server-side when stashed
  { id: "common", w: 70, openSec: 10 * 60, income: 10 },
  { id: "rare", w: 22, openSec: 30 * 60, income: 25 },
  { id: "epic", w: 7, openSec: 2 * 3600, income: 60 },
  { id: "legendary", w: 1, openSec: 8 * 3600, income: 150 },
] as const;
export const FIRST_CAPSULE_SEC = 90;      // the very first capsule ever opens inside the first session
export const INCUBATORS_START = 3;
export const INCUBATOR_PRICES = [2000, 6000];
export const WAITING_MAX = 12;
export const OPEN_NOW_ADS_PER_DAY = 5;
export const PAL_LEVEL_INCOME = 0.2;      // +20 % income per Pal level
export const AWAY_CAP_H = 8;

// ---- coins / xp
export const COIN = { stash: 25, perfect: 2, win: 30, catch: 40 } as const;
export const XP = { round: 40, stash: 20, perfect: 3, catch: 25, win: 30 } as const;
export const LEVEL_MAX = 50;
export const xpForLevel = (l: number) => Math.round(120 + 45 * (l - 1) + 6 * (l - 1) * (l - 1));

// ---- meta
export const DAILY_ORDERS = 3;
export const STREAK = [100, 150, "rare", 250, 300, "rare", "epic"] as const;
export const PARADE_MIN_START = 0;        // UTC minute window [0, 6) each hour
export const PARADE_MIN_END = 6;
export const PARADE_COIN_MULT = 2;
export const BOARD_RESET = "Mon 09:00 UTC";

// ---- ads (CrazyGames enforces 3 min between midgames on its side too)
export const MIDGAME_EVERY_ROUNDS = 2;
export const MIDGAME_NOT_BEFORE_SEC = 180;

// ---- bots
export const BOT = {
  sneakReact: 0.55, sneakReactSd: 0.25, sneakMiss: 0.08, sneakFallsForWhistle: 0.30,
  sneakSprintIfInspectorFarther: 14,
  inspPerception: 14, inspConeDeg: 140, inspThreshold: 55, inspThresholdGentle: 75,
  inspReactMin: 0.4, inspReactMax: 1.0, inspAimErr: 0.35,
  inspWhistleMin: 30, inspWhistleMax: 60,
  susBlunder: 60, susSprintPerSec: 35, susGrab: 30, susStash: 20, susGlint: 35, susDecayPerSec: 2,
} as const;

// =====================================================================================================================
// ---- SIM / NET additions (gameplay-code, 2026-10-04). Everything the simulation, the bots and the wire need that the
//      design bible leaves implicit. DESIGN.md numbers above stay the source; these fill in the mechanics around them.

// ---- the square (metres; x east, z south, y up; the playable square is centred on the fountain)
export const SQUARE_HALF = 18;            // playable area is [-18, 18] on both axes (≈36 × 36 m)
export const NAV_CELL = 0.5;              // nav grid resolution
export const NAV_CLEAR = 0.05;            // extra clearance on top of BODY_R when rasterising obstacles

// ---- steering
export const SEPARATION_R = 0.85;         // NPCs ease apart inside this distance
export const SEPARATION_K = 1.4;          // m/s of push at full overlap
export const ARRIVE_R = 0.3;              // a waypoint counts as reached inside this
export const STUCK_SEC = 2.5;             // an NPC that makes no progress this long re-plans
export const MOVE_DEADZONE = 0.2;         // stick magnitude under this counts as "not moving" (FREEZE, grab)

// ---- NPC routines (seconds)
export const BROWSE_MIN = 3, BROWSE_MAX = 7;
export const SIT_MIN = 6, SIT_MAX = 14;
export const CHAT_MIN = 5, CHAT_MAX = 10;
export const LOOK_MIN = 2, LOOK_MAX = 5;
export const SHOELACE_SEC = 2.0;
export const SHOELACE_P = 0.02;           // chance per POI hop to stop and tie a lace on the spot
export const SHOELACE_DRAIN_P = 0.35;     // ...and at a drain POI
export const ROUTINE_W = { browse: 30, sit: 18, chat: 14, look: 22, rim: 10, drain: 6 } as const;   // POI choice weights
export const CHAT_RECRUIT_R = 12;         // a chat partner is recruited from this far away at most
export const GETUP_SEC = 1.0;             // popped innocent: ragdoll (TUMBLE_SEC) → getup → angry (ANGRY_SEC)
export const REVEAL_SEC = 1.8;            // a caught Sneak's true self flies as a ragdoll this long, then is gone
export const POINT_SEC = 0.5;             // the Inspector's pop pose

// ---- the bus (bus stop on the south edge)
export const BUS_EVERY = 20;              // a timetable bus every 20 s (it also comes early for a waiting respawn)
export const BUS_DRIVE = 2.0;             // pulling in / pulling out
export const BUS_DOCK = 4.0;              // doors open
export const BUS_DROP_MIN = 1, BUS_DROP_MAX = 3;
export const BUS_EXIT_GAP = 0.5;          // riders step off one by one
export const NPC_AWAY_START = 2;          // of NPC_COUNT, this many start the round riding the bus

// ---- calls / whistle extras
export const CALL_FIRST_MAX = 12;         // first call of a normal round lands in [CALL_FIRST_MIN, CALL_FIRST_MAX]
export const WHISTLE_WINDOW = 1.5;        // a whistle can fool a Sneak this long after it sounds
export const WHISTLE_FOOL_HOLD = 1.5;     // a fooled Sneak performs the call alone this long
export const GLINT_FLASH = 0.5;           // the glint flag stays up this long in snapshots

// ---- inspector extras
export const POP_REWIND_SEC = 0.35;       // the server accepts an aim point against where the target was this recently
export const POP_BODY_H = 1.4;            // pick cylinder height
export const POP_SLACK = 0.1;             // quantisation / interpolation slack on the pick radius

// ---- capsules extras
export const CAPSULES_START = 0;          // capsules on the stalls when a round begins (the first pops up at CAPSULE_RESPAWN)
export const ONBOARD_CAPSULES_START = 2;  // ...a newcomer's round starts with two, so a stall can pulse at them straight away

// ---- parade
export const FLOAT_LOOP_R = 8.2;          // the float circles the fountain on this radius
export const FLOAT_SPEED = 0.9;           // m/s
export const FLOAT_R = 1.5;               // collision radius of the float
export const GOLDEN_GRAB_RANGE = 2.3;     // grab range around the float's centre
export const CONGA_N = 8;                 // NPCs that join the conga behind the float
export const CONGA_GAP = 0.9;             // spacing along the loop

// ---- onboarding round (a newcomer's first round)
export const ONBOARD_FIRST_CALL = 6.0;    // the first call lands at exactly this
export const ONBOARD_SCRIPT_POP = 30;     // the bot Inspector pops an innocent around here ("OOPS!")
export const ONBOARD_SAFE = 45;           // ...and pops nobody else before this
// first minute (2026-10-08, CG day-1 funnel: 88 % of newcomers reached play but only 22 % ever grabbed a capsule; non-grabbers
// left ~55 s into the round). A newcomer's first round now puts a capsule in their hands within seconds:
export const ONBOARD_SPAWN_DIST = 1.0;     // the newcomer starts AT the stall's counter (inside grab range): the first thing to do is press ACTION
// 2026-10-08 night read: cg7 doubled the grab (20 -> 40 %) but 60 % of newcomers still left before acting. The first grab and the
// first stash of a newcomer's round are a quick PRESS, not a 1 s hold:
export const ONBOARD_GRAB_SEC = 0.25;
export const ONBOARD_STASH_SEC = 0.35;
export const ONBOARD_RESERVE = 40;        // that stall's capsule is theirs alone for this long (or until they grab it)
export const ONBOARD_FIRST_CALL_MAX = 25; // the first Crier call waits for their first stash (+3 s) or this, whichever is first
export const ONBOARD_BOT_STASH_AFTER = 30; // bot Sneaks do not stash before this, so the newcomer's stash is the vault's first

// ---- net
export const RECONNECT_SEC = 15;
export const INPUT_QUEUE_MAX = 4;         // inputs buffered per seat on the server before the oldest are dropped
export const ROOM_CODE_LEN = 6;
export const PRED_SMOOTH_MS = 120;        // own-body reconciliation error melts away over this
export const TELEMETRY_PER_MIN = 30;      // rows a minute per address

// ---- bots, beyond the BOT block above (same units)
export const BOT_EXTRA = {
  sneakCoverMin: 4, sneakCoverMax: 12,    // seconds a bot Sneak spends looking normal between jobs
  sneakWary: 0.75,                         // chance a bot Sneak waits out the Inspector's gaze before grabbing/stashing
  sneakFirstDelayMax: 10,                  // a round's first job starts after up to this much cover
  sneakNoticeMin: 2, sneakNoticeMax: 6,    // a bot notices a capsule that just popped onto a stall this late (people first)
  inspGentleReactMult: 1.6,                // gentle mode: slower trigger finger
  inspWhistleCrowd: 4,                     // whistles only when at least this many bodies are in view
  inspThinkHz: 10,                         // perception updates per second
  inspPatrolLook: 2.5,                     // seconds the Inspector stops to look around at a patrol point
  inspChaseScore: 20,                      // walks toward a body whose suspicion is above this
  inspStandoff: 7,                         // ...and closes to this distance before popping
  inspWatchStall: 4, inspWatchDrain: 2,    // patrol weights: watch a stocked stall / a drain / (1) the plaza
} as const;

// ---- the person-like Sneak used by the human-model gate (tools/sim/human.ts)
export const HUMAN_MODEL = { react: 0.6, reactSd: 0.25, miss: 0.10, sprintNeverWithin: 18, fallsForWhistle: 0.35, noticeMin: 0.5, noticeMax: 1.5 } as const;
